'use client'

import { useState, useRef, useEffect } from 'react'
import ReactPlayer from 'react-player'
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Minimize, 
  SkipBack,
  SkipForward,
  RotateCcw,
  RotateCw
} from 'lucide-react'

interface VideoPlayerProps {
  animeId: string
  title: string
  episode?: number
  onNext?: () => void
  onPrevious?: () => void
}

export function VideoPlayer({ animeId, title, episode = 1, onNext, onPrevious }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const [played, setPlayed] = useState(0)
  const [duration, setDuration] = useState(0)
  const [seeking, setSeeking] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const playerRef = useRef<ReactPlayer>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const controlsTimeoutRef = useRef<NodeJS.Timeout>()

  // Fetch video URL when component mounts or episode changes
  useEffect(() => {
    const fetchVideoUrl = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const response = await fetch(`/api/anime/${animeId}/episodes/${episode}`)
        
        if (!response.ok) {
          throw new Error('Failed to fetch episode')
        }
        
        const episodeData = await response.json()
        
        if (episodeData.sources && episodeData.sources.length > 0) {
          // Get the best quality source
          const bestSource = episodeData.sources.find((s: any) => s.quality === '1080p') || 
                            episodeData.sources.find((s: any) => s.quality === '720p') || 
                            episodeData.sources[0]
          setVideoUrl(bestSource.url)
        } else {
          throw new Error('No video sources found')
        }
      } catch (error) {
        console.error('Error fetching video:', error)
        setError('Failed to load video. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchVideoUrl()
  }, [animeId, episode])

  // Auto-hide controls
  useEffect(() => {
    const resetControlsTimeout = () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
      
      setShowControls(true)
      
      if (playing) {
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false)
        }, 3000)
      }
    }

    resetControlsTimeout()
    
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    }
  }, [playing])

  const handlePlayPause = () => {
    setPlaying(!playing)
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value)
    setVolume(newVolume)
    setMuted(newVolume === 0)
  }

  const handleToggleMute = () => {
    setMuted(!muted)
  }

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPlayed = parseFloat(e.target.value)
    setPlayed(newPlayed)
    setSeeking(true)
  }

  const handleSeekMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
    setSeeking(false)
    const newPlayed = parseFloat((e.target as HTMLInputElement).value)
    playerRef.current?.seekTo(newPlayed)
  }

  const handleProgress = (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
    if (!seeking) {
      setPlayed(state.played)
    }
  }

  const handleDuration = (duration: number) => {
    setDuration(duration)
  }

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen()
      setFullscreen(true)
    } else {
      document.exitFullscreen()
      setFullscreen(false)
    }
  }

  const handleKeyPress = (e: KeyboardEvent) => {
    switch (e.code) {
      case 'Space':
        e.preventDefault()
        handlePlayPause()
        break
      case 'ArrowLeft':
        e.preventDefault()
        playerRef.current?.seekTo(played - 10/duration, 'seconds')
        break
      case 'ArrowRight':
        e.preventDefault()
        playerRef.current?.seekTo(played + 10/duration, 'seconds')
        break
      case 'KeyF':
        e.preventDefault()
        handleFullscreen()
        break
      case 'KeyM':
        e.preventDefault()
        handleToggleMute()
        break
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [played, duration])

  const formatTime = (seconds: number) => {
    const date = new Date(seconds * 1000)
    const hh = date.getUTCHours()
    const mm = date.getUTCMinutes()
    const ss = date.getUTCSeconds().toString().padStart(2, '0')
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`
    }
    return `${mm}:${ss}`
  }

  return (
    <div 
      ref={containerRef}
      className="video-player relative bg-black rounded-lg overflow-hidden group"
      onMouseMove={() => setShowControls(true)}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      {videoUrl && !loading && !error && (
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          width="100%"
          height="100%"
          playing={playing}
          volume={muted ? 0 : volume}
          playbackRate={playbackRate}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onEnded={() => onNext?.()}
          onReady={() => setLoading(false)}
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload'
              }
            }
          }}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-white text-lg">Loading episode...</p>
            <p className="text-gray-400 text-sm mt-2">
              Fetching video sources...
            </p>
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {error && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
              <span className="text-white text-2xl">⚠</span>
            </div>
            <p className="text-white text-lg mb-2">Unable to load episode</p>
            <p className="text-gray-400 text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className={`video-controls ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        {/* Episode Info */}
        <div className="mb-4">
          <h3 className="text-white font-semibold text-lg">{title}</h3>
          {episode && (
            <p className="text-gray-300 text-sm">Episode {episode}</p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <input
            type="range"
            min="0"
            max="1"
            step="0.001"
            value={played}
            onChange={handleSeekChange}
            onMouseUp={handleSeekMouseUp}
            className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs text-gray-300 mt-1">
            <span>{formatTime(played * duration)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Previous Episode */}
            {onPrevious && (
              <button
                onClick={onPrevious}
                className="text-white hover:text-blue-400 transition-colors"
              >
                <SkipBack className="w-6 h-6" />
              </button>
            )}

            {/* Rewind 10s */}
            <button
              onClick={() => playerRef.current?.seekTo(played - 10/duration, 'seconds')}
              className="text-white hover:text-blue-400 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>

            {/* Play/Pause */}
            <button
              onClick={handlePlayPause}
              className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full transition-colors"
            >
              {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            </button>

            {/* Forward 10s */}
            <button
              onClick={() => playerRef.current?.seekTo(played + 10/duration, 'seconds')}
              className="text-white hover:text-blue-400 transition-colors"
            >
              <RotateCw className="w-5 h-5" />
            </button>

            {/* Next Episode */}
            {onNext && (
              <button
                onClick={onNext}
                className="text-white hover:text-blue-400 transition-colors"
              >
                <SkipForward className="w-6 h-6" />
              </button>
            )}

            {/* Volume */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleToggleMute}
                className="text-white hover:text-blue-400 transition-colors"
              >
                {muted || volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Playback Speed */}
            <select
              value={playbackRate}
              onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
              className="bg-gray-800 text-white text-sm rounded px-2 py-1"
            >
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>

            {/* Fullscreen */}
            <button
              onClick={handleFullscreen}
              className="text-white hover:text-blue-400 transition-colors"
            >
              {fullscreen ? (
                <Minimize className="w-5 h-5" />
              ) : (
                <Maximize className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid #1f2937;
        }

        .slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid #1f2937;
        }
      `}</style>
    </div>
  )
}
